import { __assign, __awaiter, __generator } from "tslib";
import _isEmpty from 'lodash/isEmpty';
import _maxBy from 'lodash/maxBy';
import _get from 'lodash/get';
import _difference from 'lodash/difference';
import * as messageDraftInitialStateCache from '../state/helpers/message-draft.initial-state-cache';
import * as chatEventEmitter from '../services/chat-event-emitter.service';
import Message from '../domain/message';
import { createGetChatroomMessagesSelector } from '../state/reducer/selector.messages';
import { getChatRoom } from '../state/stateReader';
import getRoomListener from './room-listener';
import { getMissingParticipantIds } from '../state/helpers/participants.helper';
import { getLastMessageSequence } from '../state/reducer/selector.chatrooms';
import { buildEnrichedChatroomFromState } from '../domain/enriched-chat-room';
import { chatSdk } from './chat-sdk';
import { Chatroom } from '../domain/chat-room';
import MessageOptions from '../domain/message-options';
import { calculateNextUnreadableSequence } from './message-sequence';
import { SdkExperiment, STAR_LABEL_ID } from '../constants/constants';
var ChatroomApi = /** @class */ (function () {
    function ChatroomApi(_a) {
        var store = _a.store, chatroomsActions = _a.chatroomsActions, subscriptionService = _a.subscriptionService, currentUser = _a.currentUser, enrichedChatroom = _a.enrichedChatroom;
        this.store = store;
        this.chatroomsActions = chatroomsActions;
        this.subscriptionService = subscriptionService;
        this.currentUser = currentUser;
        this.enrichedChatroom = enrichedChatroom;
    }
    ChatroomApi.prototype.subscribeToChatRoom = function (_a) {
        var _this = this;
        var chatroomId = _a.chatroomId, onChange = _a.onChange, onNewMessage = _a.onNewMessage, onInitialFetchDone = _a.onInitialFetchDone, onError = _a.onError, initialCursor = _a.initialCursor, cursorStrategy = _a.cursorStrategy;
        var _b = this, store = _b.store, chatroomsActions = _b.chatroomsActions;
        messageDraftInitialStateCache.invalidate(chatroomId);
        var _notifyChanges = getRoomListener(chatroomId);
        _notifyChanges({ store: store, onChange: onChange, onNewMessage: onNewMessage, onError: onError });
        var removeStoreSubscription = store.subscribe(function () { return _notifyChanges({ store: store, onChange: onChange, onNewMessage: onNewMessage, onError: onError }); });
        var getData = this._createDataFetcher(chatroomId, onInitialFetchDone, initialCursor, cursorStrategy);
        setTimeout(function () { return getData(); }, 0); // defer to next event loop so the subscriber could process existing data
        var removeFromSubscriptionService = this._registerSubscription(chatroomId, getData);
        var fetchMissingParticipantsDisplayData = function (enrichedChatroom) {
            var participantIdsFromIndicationsDto = enrichedChatroom.indications.lastMessageIndications.map(function (indication) { return indication.participantId; });
            var participantIdsFromParticipantsDto = enrichedChatroom.participants.items.map(function (participant) { return participant.id; });
            if (_isEmpty(_difference(participantIdsFromIndicationsDto, participantIdsFromParticipantsDto))) {
                return;
            }
            var missingParticipantIdsFromState = getMissingParticipantIds(participantIdsFromIndicationsDto);
            if (!_isEmpty(missingParticipantIdsFromState)) {
                store.dispatch(chatroomsActions.action$fetchRoomParticipantsDisplayData(chatroomId, missingParticipantIdsFromState));
            }
        };
        chatEventEmitter.addListener(chatEventEmitter.CHAT_INTERNAL_EVENTS.FETCH_ENRICHED_CHATROOM_COMPLETED, fetchMissingParticipantsDisplayData);
        var fetchMessageErrorIndications = function () {
            _this.store.dispatch(_this.chatroomsActions.fetchMessageErrorIndications(chatroomId));
        };
        return {
            fetchMore: function (cursor, cursorStrategy) {
                store.dispatch(chatroomsActions.fetchRoomMessages(chatroomId, cursor, cursorStrategy)).then(function () {
                    fetchMessageErrorIndications();
                });
            },
            unsubscribe: function () {
                removeStoreSubscription();
                removeFromSubscriptionService();
                chatEventEmitter.removeListener(chatEventEmitter.CHAT_INTERNAL_EVENTS.FETCH_ENRICHED_CHATROOM_COMPLETED, fetchMissingParticipantsDisplayData);
            },
        };
    };
    ChatroomApi.prototype._registerSubscription = function (chatroomId, getData) {
        var subscriptionIdentifier = { chatroomId: chatroomId };
        return this.subscriptionService.registerSubscription(subscriptionIdentifier, getData);
    };
    ChatroomApi.prototype._createDataFetcher = function (chatroomId, onInitialFetchDone, cursor, cursorStrategy) {
        var _this = this;
        // called on initial room fetch AND on refresh (via subscriptionService)
        return function () {
            return Promise.all([
                _this.store.dispatch(_this.chatroomsActions.action$fetchRoom(chatroomId, _this.enrichedChatroom)),
                _this.store.dispatch(_this.chatroomsActions.fetchRoomMessages(chatroomId, cursor, cursorStrategy)),
            ])
                .then(function () {
                _this.store.dispatch(_this.chatroomsActions.fetchMessageErrorIndications(chatroomId));
                if (onInitialFetchDone) {
                    var state = _this.store.getState();
                    var chatroom = buildEnrichedChatroomFromState(state, chatroomId);
                    onInitialFetchDone(chatroom);
                }
            })
                .catch(function (e) {
                console.error('CHAT4893 Error on room initial fetch', e);
            });
        };
    };
    ChatroomApi.prototype.getChatroom = function (_a) {
        var chatroomId = _a.chatroomId;
        return __awaiter(this, void 0, void 0, function () {
            var chatRoom;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        chatRoom = getChatRoom(this.store.getState(), chatroomId);
                        if (!_isEmpty(chatRoom)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.store.dispatch(this.chatroomsActions.action$fetchRoom(chatroomId, this.enrichedChatroom))];
                    case 1:
                        _b.sent();
                        chatRoom = getChatRoom(this.store.getState(), chatroomId);
                        _b.label = 2;
                    case 2: return [2 /*return*/, new Chatroom(chatRoom)];
                }
            });
        });
    };
    ChatroomApi.prototype.setMessagesRead = function (_a) {
        var chatroomId = _a.chatroomId;
        var lastReadMessageSequence = getLastMessageSequence(this.store.getState(), chatroomId);
        if (lastReadMessageSequence) {
            return this.store.dispatch(this.chatroomsActions.action$setMessagesRead(chatroomId, lastReadMessageSequence));
        }
    };
    ChatroomApi.prototype.setRoomsMessagesRead = function (chatroomIds) {
        var state = this.store.getState();
        var chatroomIdWithMostRecentMessageSequence = _maxBy(chatroomIds, function (chatroomId) {
            return _get(getChatRoom(state, chatroomId), 'lastMessage.sequence');
        });
        var mostRecentLastMessageSequence = chatroomIdWithMostRecentMessageSequence &&
            _get(getChatRoom(state, chatroomIdWithMostRecentMessageSequence), 'lastMessage.sequence');
        if (mostRecentLastMessageSequence) {
            this.store.dispatch(this.chatroomsActions.action$setRoomsMessagesRead(chatroomIds, mostRecentLastMessageSequence));
        }
    };
    ChatroomApi.prototype.setLastMessageUnread = function (_a) {
        var chatroomId = _a.chatroomId;
        var state = this.store.getState();
        var messages = createGetChatroomMessagesSelector(chatroomId)(state);
        var chatroom = getChatRoom(state, chatroomId);
        var unreadableSequence = calculateNextUnreadableSequence(chatroom.lastReadMessageSequence, messages, this.currentUser.userId);
        if (unreadableSequence) {
            return this.store.dispatch(this.chatroomsActions.action$setLastMessageUnread(chatroomId, unreadableSequence));
        }
    };
    ChatroomApi.prototype.getMessageWithSender = function (_a) {
        var dto = _a.dto;
        var currentUser = this.currentUser;
        var dtoWithCurrentUserDetails = __assign(__assign({}, dto), { sender: dto.sender || { userId: currentUser.userId, type: currentUser.type }, senderDisplayData: currentUser.displayData, appId: chatSdk.appId });
        return new Message(dtoWithCurrentUserDetails);
    };
    ChatroomApi.prototype.sendMessage = function (_a) {
        var dto = _a.dto;
        var _b = this, store = _b.store, chatroomsActions = _b.chatroomsActions;
        var messageToSend = this.getMessageWithSender({ dto: dto });
        var sendMessageAction = chatroomsActions.action$sendMessage(messageToSend);
        return store.dispatch(sendMessageAction);
    };
    ChatroomApi.prototype.addMessage = function (_a) {
        var _b;
        var dto = _a.dto, transit = _a.transit;
        return __awaiter(this, void 0, void 0, function () {
            var _c, currentUser, store, chatroomsActions, messageIsFromCurrentUser, message, chatroomId, chatroomById, isEnrichedChatroom, fetchPromise;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        _c = this, currentUser = _c.currentUser, store = _c.store, chatroomsActions = _c.chatroomsActions;
                        messageIsFromCurrentUser = !((_b = dto.sender) === null || _b === void 0 ? void 0 : _b.userId) || dto.sender.userId == currentUser.userId;
                        message = Object.assign(dto, { transit: transit }, messageIsFromCurrentUser
                            ? {
                                senderDisplayData: currentUser.displayData,
                                sender: { userId: currentUser.userId },
                            }
                            : {});
                        chatroomId = dto.chatroomId;
                        chatroomById = store.getState().chatrooms.byId[chatroomId];
                        if (!(!chatroomById || !chatroomById.id)) return [3 /*break*/, 2];
                        isEnrichedChatroom = chatSdk.isExperimentOn(SdkExperiment.EnrichedChatroom);
                        fetchPromise = store.dispatch(chatroomsActions.action$fetchRoom(chatroomId, isEnrichedChatroom));
                        return [4 /*yield*/, fetchPromise];
                    case 1:
                        _d.sent();
                        _d.label = 2;
                    case 2: return [2 /*return*/, store.dispatch(chatroomsActions.action$addMessage(message, true, store.getState().chatrooms.byId[chatroomId]))];
                }
            });
        });
    };
    ChatroomApi.prototype.deleteMessage = function (messageId, persist) {
        if (persist === void 0) { persist = false; }
        var _a = this, store = _a.store, chatroomsActions = _a.chatroomsActions;
        var deleteMessageAction = chatroomsActions.action$deleteMessage(messageId, persist);
        return store.dispatch(deleteMessageAction);
    };
    ChatroomApi.prototype.resendMessage = function (messageId) {
        var _a = this, store = _a.store, chatroomsActions = _a.chatroomsActions;
        var resendMessageAction = chatroomsActions.action$resendMessage(messageId);
        return store.dispatch(resendMessageAction);
    };
    ChatroomApi.prototype.deleteLabel = function (_a) {
        var chatroomId = _a.chatroomId, labelId = _a.labelId;
        var _b = this, store = _b.store, chatroomsActions = _b.chatroomsActions;
        var deleteLabelAction = chatroomsActions.action$removeRoomLabels([{ chatroomId: chatroomId, labelId: labelId }], true);
        return store.dispatch(deleteLabelAction);
    };
    ChatroomApi.prototype.unstarRoom = function (chatroomId) {
        return this.deleteLabel({ chatroomId: chatroomId, labelId: STAR_LABEL_ID });
    };
    ChatroomApi.prototype.starRoom = function (chatroomId) {
        var _a = this, store = _a.store, chatroomsActions = _a.chatroomsActions;
        var updateLabelAction = chatroomsActions.action$updateRoomLabel({ chatroomId: chatroomId, labelId: STAR_LABEL_ID });
        return store.dispatch(updateLabelAction);
    };
    ChatroomApi.prototype.buttonInteraction = function (button, chatroomId) {
        var _a = this, store = _a.store, currentUser = _a.currentUser, chatroomsActions = _a.chatroomsActions;
        if (button.payload) {
            var message = Message.fromText({
                text: button.text,
                chatroomId: chatroomId,
                userId: currentUser.userId,
                userType: currentUser.type,
                summary: button.text,
                isEphemeral: true,
                isSilent: true,
                appId: chatSdk.appId,
            });
            var messageOptions = new MessageOptions({
                chatAction: __assign({ actionType: 'BUTTON_INTERACTION' }, button.payload),
            });
            return store.dispatch(chatroomsActions.action$sendPostbackMessage(message, messageOptions));
        }
    };
    return ChatroomApi;
}());
export default ChatroomApi;
